import React from 'react';
import { motion } from 'framer-motion';
import { Analytics } from '../../types/business';

interface AnalyticsDashboardProps {
  analytics: Analytics;
  loading: boolean;
}

export const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({
  analytics,
  loading
}) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR'
    }).format(amount);
  };

  const calculateConversionRate = (): string => {
    if (analytics.monthlyBookings === 0) return "0";
    return ((analytics.completedBookings / analytics.monthlyBookings) * 100).toFixed(1);
  };

  const getConversionRateNumber = (): number => {
    if (analytics.monthlyBookings === 0) return 0;
    return (analytics.completedBookings / analytics.monthlyBookings) * 100;
  };

  const metrics = [
    {
      title: "Total Inventory",
      value: analytics.totalInventory,
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4-8-4m16 0v10l-8 4-8-4V7" />
        </svg>
      ),
      color: "blue",
      trend: analytics.totalInventory > 50 ? "up" : "down"
    },
    {
      title: "Low Stock Items",
      value: analytics.lowStockItems,
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      ),
      color: analytics.lowStockItems > 5 ? "red" : "yellow",
      trend: analytics.lowStockItems > 5 ? "up" : "stable"
    },
    {
      title: "Pending Bookings",
      value: analytics.pendingBookings,
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      color: analytics.pendingBookings > 0 ? "orange" : "gray",
      trend: analytics.pendingBookings > 0 ? "up" : "stable"
    },
    {
      title: "Monthly Bookings",
      value: analytics.monthlyBookings,
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      color: "purple",
      trend: analytics.monthlyBookings > analytics.completedBookings ? "up" : "stable"
    },
    {
      title: "Completed Bookings",
      value: analytics.completedBookings,
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      color: "green",
      trend: analytics.completedBookings > 0 ? "up" : "stable"
    },
    {
      title: "Revenue Estimate",
      value: formatCurrency(analytics.revenue),
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
        </svg>
      ),
      color: "green",
      trend: analytics.revenue > 0 ? "up" : "stable",
      isValue: true
    },
    {
      title: "Conversion Rate",
      value: `${calculateConversionRate()}%`,
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      ),
      color: getConversionRateNumber() > 75 ? "green" : getConversionRateNumber() > 50 ? "yellow" : "red",
      trend: getConversionRateNumber() > 50 ? "up" : "down",
      isValue: true
    }
  ];

  const getColorClasses = (color: string) => {
    const colors = {
      blue: "bg-blue-500 text-blue-600 bg-blue-50",
      red: "bg-red-500 text-red-600 bg-red-50",
      yellow: "bg-yellow-500 text-yellow-600 bg-yellow-50",
      green: "bg-green-500 text-green-600 bg-green-50",
      orange: "bg-orange-500 text-orange-600 bg-orange-50",
      purple: "bg-purple-500 text-purple-600 bg-purple-50",
      gray: "bg-gray-500 text-gray-600 bg-gray-50"
    };
    return colors[color as keyof typeof colors] || colors.gray;
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case "up":
        return (
          <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M3.293 9.707a1 1 0 010-1.414l6-6a1 1 0 011.414 0l6 6a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L4.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        );
      case "down":
        return (
          <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 10.293a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-6-6a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l4.293-4.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-white rounded-xl shadow-lg p-6"
      >
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Analytics Dashboard</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(8)].map((_, index) => (
            <div key={index} className="animate-pulse">
              <div className="bg-gray-200 rounded-lg p-6 h-32"></div>
            </div>
          ))}
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-lg p-6"
    >
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Analytics Dashboard</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metrics.map((metric, index) => {
          const colorClasses = getColorClasses(metric.color).split(' ');
          const iconBg = colorClasses[0];
          const textColor = colorClasses[1];
          const bgColor = colorClasses[2];
          
          return (
            <motion.div
              key={metric.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`${bgColor} rounded-lg p-6 border border-gray-200 hover:shadow-md transition-shadow`}
            >
              <div className="flex items-center justify-between">
                <div className={`${iconBg} p-3 rounded-lg`}>
                  <div className="text-white">
                    {metric.icon}
                  </div>
                </div>
                {getTrendIcon(metric.trend)}
              </div>
              
              <div className="mt-4">
                <h3 className="text-sm font-medium text-gray-600 mb-1">
                  {metric.title}
                </h3>
                <p className={`text-2xl font-bold ${textColor}`}>
                  {metric.isValue ? String(metric.value) : Number(metric.value).toLocaleString()}
                </p>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Quick Insights */}
      <div className="mt-8 bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="bg-white rounded-lg p-4">
            <div className="flex items-center">
              <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
              <span className="text-gray-600">
                {analytics.totalInventory > 100 ? "Strong" : analytics.totalInventory > 50 ? "Good" : "Low"} inventory levels
              </span>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4">
            <div className="flex items-center">
              <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
              <span className="text-gray-600">
                {getConversionRateNumber() > 75 ? "Excellent" : getConversionRateNumber() > 50 ? "Good" : "Needs improvement"} conversion rate
              </span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};